import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, STATUS_LABELS, ANLAGEN_TYPEN } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AnmeldungForm } from "@/components/AnmeldungForm";
import { FileText, ExternalLink, Plus } from "lucide-react";

export function ProjectAnmeldungen({ project, canCreate }) {
  const [items, setItems] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [auftraggeber, setAuftraggeber] = useState([]);

  const load = useCallback(() => {
    api.get(`/projects/${project.id}/anmeldungen`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [project.id]);
  useEffect(() => { setItems(null); load(); }, [load]);

  const openForm = () => {
    if (!auftraggeber.length) api.get("/auftraggeber").then((r) => setAuftraggeber(r.data || [])).catch(() => {});
    setFormOpen(true);
  };

  const seed = useMemo(() => ({
    kunde_nachname: project.kunde_name || "", kunde_strasse: project.kunde_adresse || "",
    kunde_plz: project.kunde_plz || "", kunde_ort: project.kunde_ort || "",
    auftraggeber_id: project.auftraggeber_id || "none", projekt_id: project.id,
  }), [project.id, project.kunde_name, project.kunde_adresse, project.kunde_plz, project.kunde_ort, project.auftraggeber_id]);

  if (!items) return null;

  return (
    <div className="mt-3 rounded-md border border-border p-3" data-testid="project-anmeldungen">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-sm font-medium flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" /> Verknüpfte Netzanmeldungen ({items.length})
        </div>
        {canCreate && (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={openForm} data-testid="project-anmeldung-add">
            <Plus className="h-3 w-3 mr-1" /> Weitere Anmeldung
          </Button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-muted-foreground" data-testid="project-anmeldungen-empty">Keine Anmeldung verknüpft.</div>
      ) : (
        <ul className="divide-y divide-border/60">
          {items.map((a) => (
            <li key={a.id} className="flex items-center gap-3 py-1.5 text-xs" data-testid={`project-anmeldung-${a.id}`}>
              <span className="rounded bg-secondary px-1.5 py-0.5 font-medium shrink-0" data-testid={`project-anmeldung-status-${a.id}`}>
                {STATUS_LABELS[a.status] || a.status}
              </span>
              <span className="flex-1 min-w-0 truncate">
                {(a.anlagen_typen || []).map((t) => ANLAGEN_TYPEN[t] || t).join(", ") || "—"}
                {a.anmeldedatum ? ` · ${a.anmeldedatum}` : ""}
              </span>
              <Link to={`/anmeldungen?deeplink=${a.id}`} className="inline-flex items-center gap-1 text-primary hover:underline shrink-0" data-testid={`project-anmeldung-link-${a.id}`}>
                Öffnen <ExternalLink className="h-3 w-3" />
              </Link>
            </li>
          ))}
        </ul>
      )}
      {canCreate && (
        <AnmeldungForm open={formOpen} onOpenChange={setFormOpen} initial={seed} onSaved={load} auftraggeber={auftraggeber} />
      )}
    </div>
  );
}
